/**
 * VIB-758: Sentralt cache-registry for React Query-invalidation etter
 * mutations. Hver DB-tabell mapper til de queryKeys som leser fra den.
 * Mutation-hooks kaller invalidateFor(queryClient, table, context?) som
 * slår opp i denne registry-en og invaliderer alle berørte keys.
 *
 * Når du legger til en ny useQuery-hook som leser fra en eksisterende
 * tabell, registrer queryKey-prefikset her så eksisterende mutations
 * automatisk invaliderer den.
 *
 * Konvensjon: entityField-navn er camelCase (projectId, userId, ...) og
 * representerer hva mutation-hook må passere som context når den kaller
 * invalidateFor. Hvis mutation-hook bruker snake_case-variabler internt,
 * må den mappe `{ projectId: vars.project_id }` ved kallet.
 */

export type AffectedKey =
  | { readonly key: readonly [string] }
  | { readonly key: readonly [string]; readonly entityField: string };

export type TableName =
  | "absences"
  | "access_groups"
  | "access_group_members"
  | "document_editors"
  | "document_folder_grants"
  | "document_images"
  | "announcements"
  | "budget_line_assignments"
  | "budget_line_freelancers"
  | "budget_line_baseline"
  | "budget_lines"
  | "chat_members"
  | "checklist_templates"
  | "chat_messages"
  | "chat_rooms"
  | "message_reactions"
  | "commission_payouts"
  | "company_holidays"
  | "crm_agent_runs"
  | "crm_gmail_sync_state"
  | "crm_meetings"
  | "crm_outbox"
  | "customer_activities"
  | "customer_company_info"
  | "customer_contacts"
  | "customer_monitoring_sources"
  | "customer_owners"
  | "customer_signals"
  | "customers"
  | "departments"
  | "document_folders"
  | "documents"
  | "equipment_booking_items"
  | "equipment_bookings"
  | "equipment_brands"
  | "equipment_categories"
  | "equipment_enrichment_runs"
  | "equipment_items"
  | "equipment_media"
  | "equipment_products"
  | "equipment_service_tickets"
  | "event_log"
  | "extra_contracts"
  | "extra_form_tokens"
  | "feedback"
  | "follow_ups"
  | "freelancer_comment_attachments"
  | "freelancer_comments"
  | "freelancer_contacts"
  | "freelancer_discipline_links"
  | "freelancer_disciplines"
  | "freelancer_reviews"
  | "freelancer_suppliers"
  | "freelancers"
  | "import_log"
  | "invoices"
  | "lead_attempts"
  | "lead_lists"
  | "lead_queue_settings"
  | "leads"
  | "maaltall_check_feedback"
  | "location_images"
  | "location_types"
  | "locations"
  | "meeting_summaries"
  | "meeting_transcripts"
  | "monthly_closings"
  | "monthly_confirmations"
  | "notification_preferences"
  | "notifications"
  | "offer_line_templates"
  | "offer_editors"
  | "offer_workspaces"
  | "offer_idea_cards"
  | "offer_idea_groups"
  | "offer_lines"
  | "offer_sections"
  | "offer_template_lines"
  | "offer_templates"
  | "offers"
  | "opportunities"
  | "project_comment_reads"
  | "project_comments"
  | "project_documents"
  | "project_events"
  | "project_extras"
  | "project_locations"
  | "project_meetings"
  | "project_offer_metadata"
  | "project_owners"
  | "project_role_permissions"
  | "projects"
  | "recurring_agreements"
  | "role_menu_permissions"
  | "sales_unit_members"
  | "sales_units"
  | "sales_display_slides"
  | "sidebar_shortcuts"
  | "signal_agents"
  | "signal_dismissals"
  | "storage_pricing_tiers"
  | "system_config"
  | "task_comments"
  | "time_entries"
  | "timebank_transactions"
  | "upsells"
  | "user_widget_overrides"
  | "users"
  | "voucher_allocations"
  | "voucher_account_mappings"
  | "vouchers"
  | "widget_configs";

type Registry = Readonly<Record<TableName, readonly AffectedKey[]>>;

/**
 * Mapping fra DB-tabell til alle queryKeys som leser data derfra.
 *
 * Konvensjon: bruk `entityField` for keys som er prefix + ID (f.eks.
 * ['project-detail', projectId]). Bruk plain key uten entityField for
 * globale queries (f.eks. ['my-tasks']).
 *
 * Aggregat-hooks (f.eks. dashboard, employee-dashboard, pm-inbox) leser fra
 * flere tabeller og registreres derfor på hver kildetabell de avhenger av.
 */
export const CACHE_REGISTRY = {
  absences: [
    // VIB-2990: kapasitetsfordelingen i teamets faktureringsgrad.
    { key: ["capacity-breakdown"] },
    // Bredt prefix dekker både ["absences", userId] og ["absences", "pending"]
    // — flere lesere bruker andre suffix-er enn userId.
    { key: ["absences"] },
    { key: ["billing-rates"], entityField: "year" },
    { key: ["employee-billing-rate"], entityField: "userId" },
    { key: ["billing-rate-completed-tasks"], entityField: "userId" },
    { key: ["time-gaps"], entityField: "userId" },
    // VIB-2093: popup-en om manglende timeføring. Bredt prefiks — nøkkelen
    // bærer også dagens dato, så entityField ville ikke truffet.
    { key: ["missing-hours"] },
    { key: ["my-missing-days"] },
    { key: ["employee-kpis"], entityField: "userId" },
  ],
  announcements: [
    { key: ["announcements"] },
  ],
  budget_line_assignments: [
    // VIB-3024: tildelte er med i prosjektkanalen.
    { key: ["project-channels"] },
    // VIB-2990: kapasitetsfordelingen i teamets faktureringsgrad.
    { key: ["capacity-breakdown"] },
    { key: ["product-budget-deviation"] },
    { key: ["project-drill"], entityField: "projectId" },
    { key: ["my-tasks"], entityField: "userId" },
    { key: ["project-detail"], entityField: "projectId" },
    { key: ["billing-rates"], entityField: "year" },
    { key: ["bonus-goals"] },
    { key: ["top-projects"] },
    { key: ["monday-import-gap"] },
    { key: ["billing-rate-completed-tasks"], entityField: "userId" },
    { key: ["employee-billing-rate"], entityField: "userId" },
    { key: ["upcoming-tasks"] },
    { key: ["available-tasks"], entityField: "userId" },
    { key: ["employee-activity"], entityField: "userId" },
    { key: ["budget-deviations"] },
    { key: ["pm-projects"], entityField: "userId" },
    { key: ["pm-inbox"], entityField: "userId" },
    { key: ["week-tasks"] },
    { key: ["accessible-project-ids"], entityField: "userId" },
    { key: ["employees"] },
    { key: ["employee-dashboard"], entityField: "userId" },
    { key: ["employee-profile"], entityField: "userId" },
    { key: ["employee-value-contribution"], entityField: "userId" },
    { key: ["employee-kpis"], entityField: "userId" },
    { key: ["production-kpis"] },
    { key: ["project-permission"], entityField: "projectId" },
    { key: ["project-progress"] },
    { key: ["recent-completed-tasks"] },
    { key: ["task-comments"], entityField: "assignmentId" },
    { key: ["task-comment-counts"] },
  ],
  budget_line_baseline: [
    { key: ["project-report"], entityField: "projectId" },
  ],
  budget_lines: [
    { key: ["product-budget-deviation"] },
    { key: ["project-drill"], entityField: "projectId" },
    { key: ["project-detail"], entityField: "projectId" },
    // Kundekortets budsjettavvik (solgte timer per kunde).
    { key: ["customer-budgeted-hours"] },
    { key: ["upsells"], entityField: "projectId" },
    { key: ["budget-lines"], entityField: "projectId" },
    { key: ["budget-lines-for-projects"] },
    { key: ["project-hours"] },
    { key: ["project-progress"] },
    { key: ["production-kpis"] },
    { key: ["employee-dashboard"], entityField: "userId" },
    { key: ["bonus-goals"] },
    { key: ["top-projects"] },
    { key: ["budget-deviations"] },
    { key: ["employee-kpis"], entityField: "userId" },
    // VIB-1208: interne oppgaver ER budget_lines-rader. En ny/endret oppgave
    // skal oppdatere picker-listene (delt på tvers av alle brukere — derfor
    // bred invalidering uten entityField, ikke bare den som opprettet den) og
    // my-tasks (brukerspesifikk, men uten userId i budget_lines-mutasjonens
    // context — faller tilbake til bred invalidering av alle brukeres cache,
    // som er riktig her siden internoppgaver er synlige for alle).
    { key: ["internal-project-tasks"] },
    { key: ["my-tasks"], entityField: "userId" },
    // VIB-1226/VIB-1204: godkjenningsdialogens on-demand budsjettlinje-hook.
    { key: ["voucher-approval-context"], entityField: "projectId" },
    // VIB-2927: frilanshistorikken leser bare eksterne linjer. Konverteres en
    // linje til oppgave (eller tilbake), endres historikken.
    { key: ["project-freelancers"], entityField: "projectId" },
    { key: ["freelancer-project-history"] },
    { key: ["freelancer-last-used"] },
  ],
  chat_members: [
    { key: ["chat-rooms"], entityField: "userId" },
  ],
  chat_messages: [
    { key: ["chat-messages"], entityField: "roomId" },
    { key: ["chat-rooms"], entityField: "userId" },
  ],
  chat_rooms: [
    { key: ["chat-rooms"], entityField: "userId" },
  ],
  // VIB-3025: reaksjoner i Chat-rommet, nøkkel per kanal.
  message_reactions: [
    { key: ["message-reactions"], entityField: "channelKey" },
  ],
  checklist_templates: [
    { key: ["checklist-templates"] },
  ],
  commission_payouts: [
    { key: ["commission-deltas"] },
    { key: ["commission-report"] },
  ],
  company_holidays: [
    { key: ["company-holidays"] },
    { key: ["missing-hours"] },
    { key: ["my-missing-days"] },
    { key: ["payroll-basis"] },
    { key: ["billing-rates"], entityField: "year" },
    { key: ["employee-billing-rate"], entityField: "userId" },
  ],
  // CRM-1.3: kontaktlogg og siste kontakt. ["customer-activities"] dekker også
  // ["customer-activities", customerId, { limit, before }] via prefiks-matching.
  customer_activities: [
    { key: ["customer-activities"], entityField: "customerId" },
    { key: ["customer-last-contact"], entityField: "customerId" },
    { key: ["customer-detail"], entityField: "customerId" },
    // VIB-2891: kundeliste v2 leser siste kontakt for alle kunder.
    { key: ["customer-list"] },
    // VIB-2986: kontakter i dag og denne uken i Oppdag.
    { key: ["oppdag-kpis"] },
  ],
  // CRM-4.0: kundemøter. Skrives bare av edge functions (booking, synk, transkripsjon).
  // Booking endrer også ledigheten (freebusy) og møtebookerens tall.
  crm_meetings: [
    { key: ["crm-meetings"] },
    { key: ["oppdag-kpis"] },
    { key: ["customer-meetings"], entityField: "customerId" },
    { key: ["freebusy"] },
    { key: ["lead-stats"] },
    { key: ["current-lead"] },
    // VIB-3050: «Møte booket» på Leads-tavlen.
    { key: ["lead-board"] },
  ],
  // CRM-4.0: lister i leads-poolen (admin).
  lead_lists: [
    { key: ["lead-lists"] },
    { key: ["lead-list-stats"] },
    // VIB-3050: lavvann og høyvann per pool, og navnet i leadkortet.
    { key: ["pool-overview"] },
    { key: ["lead-detail"] },
  ],
  // VIB-2991: tommel opp/ned på funnene i «Dette bør du se på».
  maaltall_check_feedback: [
    { key: ["maaltall-check-feedback"] },
  ],
  // CRM-4.0: poolen er usynlig; leses bare som «mitt lead» via RPC og som statistikk.
  leads: [
    { key: ["current-lead"], entityField: "userId" },
    { key: ["customer-lead-origin"], entityField: "customerId" },
    { key: ["lead-list-stats"] },
    { key: ["lead-stats"] },
    { key: ["customer-detail"], entityField: "customerId" },
    // VIB-3020: duplikatsjekken i «Nytt lead».
    { key: ["lead-duplicate"] },
    // VIB-3032: avviste leads til vurdering.
    { key: ["rejected-leads"] },
    // VIB-3050: bookerens tavle og kø.
    { key: ["lead-board"], entityField: "userId" },
    { key: ["lead-queue-status"], entityField: "userId" },
    { key: ["lead-queue-overview"] },
    // VIB-3050 PR 3b: pooler, leads i en pool og leadkort for admin.
    { key: ["pool-overview"] },
    { key: ["pool-leads"] },
    { key: ["lead-detail"] },
  ],
  // VIB-3050: lavvann, høyvann og pooler per booker.
  lead_queue_settings: [
    { key: ["lead-queue-overview"] },
    // VIB-3050 PR 3c: «er booker» gir Leads.
    { key: ["lead-booker"] },
    { key: ["lead-queue-status"] },
    { key: ["lead-board"] },
  ],
  lead_attempts: [
    { key: ["lead-attempts"], entityField: "leadId" },
    { key: ["lead-board"] },
    { key: ["lead-queue-overview"] },
    { key: ["pool-overview"] },
    { key: ["pool-leads"] },
    { key: ["lead-detail"] },
    { key: ["lead-stats"] },
    { key: ["lead-list-stats"] },
  ],
  meeting_summaries: [
    { key: ["meeting-summary"], entityField: "meetingId" },
    { key: ["customer-meetings"] },
  ],
  meeting_transcripts: [
    { key: ["meeting-transcript"], entityField: "meetingId" },
  ],
  // CRM-3.0: kjørelogg og kostnad. Leses av kostnadspanelet i Drift.
  crm_agent_runs: [
    { key: ["crm-costs"] },
  ],
  // CRM-1.5: sync-tilstanden skrives bare av edge functions og leses ikke av noen query ennå.
  crm_gmail_sync_state: [],
  // CRM-1.3: utboksen styrer "Planlagt sendt"-visningen i kontaktloggen.
  // Bred invalidering med vilje: crm_outbox har ikke customer_id (skrives av
  // edge functions som ikke kjenner React Query), kunden finnes bare via activity_id.
  crm_outbox: [
    { key: ["customer-activities"] },
  ],
  // CRM-3.0: bedriftsinfo fra Brønnøysund. Skrives bare av edge function.
  customer_company_info: [
    { key: ["customer-company-info"], entityField: "customerId" },
    { key: ["customer-list"] },
  ],
  // CRM-3.0: overvåkning per kilde. Kostnad per kilde vises også i Drift.
  customer_monitoring_sources: [
    { key: ["monitoring-sources"], entityField: "customerId" },
    { key: ["monitored-customers"] },
    { key: ["crm-costs"] },
  ],
  // CRM-3.0: signaler. Plain-entryene dekker også ["signal-queue", userId]
  // og ["oppdag-queue"] (køen leser signaler og oppfølginger).
  customer_signals: [
    { key: ["customer-signals"], entityField: "customerId" },
    { key: ["signal-queue"] },
    { key: ["oppdag-queue"] },
    { key: ["signal-agents"] },
  ],
  signal_agents: [
    { key: ["signal-agents"] },
    { key: ["crm-costs"] },
  ],
  // «Ikke for meg» flytter signalet ut av brukerens kø.
  signal_dismissals: [
    { key: ["signal-queue"] },
    { key: ["oppdag-queue"] },
    { key: ["signal-agents"] },
  ],
  // CRM-1.1: kontaktpersoner. Kundekortet (["customer-detail"]) viser primærkontakt.
  customer_contacts: [
    { key: ["customer-contacts"], entityField: "customerId" },
    { key: ["lead-contacts"] },
    // Leadkortet viser kontaktene fra lead-payloaden.
    { key: ["current-lead"] },
    { key: ["lead-board"] },
    { key: ["lead-detail"] },
    { key: ["customer-detail"], entityField: "customerId" },
  ],
  // CRM-1.2: kundeeiere. Plain-entryen dekker også ["customer-owners", customerId].
  customer_owners: [
    { key: ["customer-owners"] },
    { key: ["customer-detail"], entityField: "customerId" },
  ],
  customers: [
    { key: ["customers"] },
    { key: ["lead-duplicate"] },
    { key: ["customer-list"] },
    { key: ["customer-detail"], entityField: "customerId" },
    // CRM-1.4: customer_status_v avhenger av customers (override), offers og projects.
    { key: ["customer-status"] },
    { key: ["project-detail"], entityField: "projectId" },
    { key: ["invoices"] },
    { key: ["pm-inbox"], entityField: "userId" },
  ],
  event_log: [
    { key: ["event-log"] },
    { key: ["equipment-history"] },
    { key: ["location-history"] },
  ],
  // VIB-2938: lokasjonsbanken. Lista leser locations_overview_v (typenavn,
  // bildeantall og forsidebilde), så alle tre tabellene invaliderer den.
  locations: [
    { key: ["locations"] },
    { key: ["location-detail"], entityField: "locationId" },
    { key: ["location-history"], entityField: "locationId" },
  ],
  location_images: [
    { key: ["locations"] },
    { key: ["location-images"], entityField: "locationId" },
    { key: ["location-history"], entityField: "locationId" },
  ],
  location_types: [
    { key: ["location-types"] },
    { key: ["locations"] },
  ],
  // VIB-2940: lokasjon på prosjekt. Triggeren logger både i prosjektets logg
  // (project_events) og lokasjonens historikk (event_log), og oversikten
  // teller koblingene.
  project_locations: [
    { key: ["project-locations"], entityField: "projectId" },
    { key: ["location-projects"], entityField: "locationId" },
    { key: ["locations"] },
    { key: ["location-history"], entityField: "locationId" },
    { key: ["project-events"], entityField: "projectId" },
  ],
  // VIB-2917: utstyrsregisteret. equipment-images er lagringsbøtta, invalidert
  // sammen med utstyret den hører til.
  departments: [
    { key: ["departments"] },
  ],
  equipment_categories: [
    { key: ["equipment-categories"] },
  ],
  equipment_items: [
    { key: ["equipment"] },
    { key: ["equipment-detail"], entityField: "equipmentId" },
    { key: ["equipment-history"], entityField: "equipmentId" },
    { key: ["equipment-images"], entityField: "equipmentId" },
    // VIB-2919: bookinglinjene viser navn, antall og status fra utstyret.
    { key: ["equipment-bookings"] },
    { key: ["project-equipment"] },
    { key: ["equipment-item-bookings"], entityField: "equipmentId" },
    { key: ["equipment-products"] },
    // VIB-2921: servicelista viser navn og status fra utstyret.
    { key: ["equipment-service"] },
  ],
  // VIB-2921: servicesaker. Triggeren setter status på utstyret, så mutasjonene
  // invaliderer equipment_items i tillegg.
  equipment_service_tickets: [
    { key: ["equipment-service"] },
    { key: ["equipment-history"], entityField: "equipmentId" },
  ],
  // VIB-2919: utstyrsbooking. Linjene leses via viewene equipment_booking_lines,
  // equipment_booking_conflicts og equipment_usage_12m, så begge tabellene
  // invaliderer de samme nøklene. Audit-triggerne skriver event_log og
  // project_events, som invalideres separat i mutasjonen.
  equipment_bookings: [
    { key: ["equipment-bookings"] },
    { key: ["equipment-booking"], entityField: "bookingId" },
    { key: ["project-equipment"], entityField: "projectId" },
    { key: ["project-equipment-conflicts"], entityField: "projectId" },
    { key: ["equipment-item-bookings"] },
    { key: ["equipment-usage"] },
  ],
  equipment_booking_items: [
    { key: ["equipment-bookings"] },
    { key: ["equipment-booking"], entityField: "bookingId" },
    { key: ["project-equipment"] },
    { key: ["project-equipment-conflicts"] },
    { key: ["equipment-item-bookings"], entityField: "equipmentId" },
    { key: ["equipment-usage"] },
  ],
  // VIB-2918: produktdata per modell. Kortet leser modell og media via product_id.
  equipment_brands: [
    { key: ["equipment-brands"] },
  ],
  equipment_products: [
    { key: ["equipment-products"] },
    { key: ["equipment-product"], entityField: "productId" },
  ],
  equipment_media: [
    { key: ["equipment-products"] },
    { key: ["equipment-product-media"], entityField: "productId" },
  ],
  equipment_enrichment_runs: [
    { key: ["equipment-products"] },
    { key: ["equipment-enrichment"], entityField: "productId" },
  ],
  extra_contracts: [
    { key: ["extra-contracts"], entityField: "projectId" },
  ],
  extra_form_tokens: [],
  feedback: [
    { key: ["admin-feedback"] },
    { key: ["admin-feedback-count"] },
    { key: ["my-feedback"] },
  ],
  // VIB-2925: frilanserbanken. Lista (["freelancers"]) viser fagfelt og vurderingssnitt,
  // så den invalideres av alle tabellene som bidrar til den.
  freelancers: [
    { key: ["freelancers"] },
    { key: ["freelancer-detail"], entityField: "freelancerId" },
    // VIB-2927: navnet vises på linjer, i Team & eierskap og i leverandørlista.
    { key: ["budget-line-freelancers"] },
    { key: ["project-freelancers"] },
    { key: ["freelance-supplier-overview"] },
  ],
  // VIB-2927: frilanser på ekstern budsjettlinje.
  budget_line_freelancers: [
    { key: ["budget-line-freelancers"], entityField: "projectId" },
    { key: ["project-freelancers"], entityField: "projectId" },
    { key: ["freelancer-project-history"], entityField: "freelancerId" },
    { key: ["freelancer-last-used"] },
    { key: ["freelance-supplier-overview"] },
  ],
  // VIB-2927: leverandør koblet til frilanser gjelder alle prosjekter.
  freelancer_suppliers: [
    { key: ["freelancer-suppliers"] },
    { key: ["freelance-supplier-overview"] },
    { key: ["project-freelancers"] },
    { key: ["freelancer-project-history"] },
    { key: ["freelancer-last-used"] },
  ],
  freelancer_disciplines: [
    { key: ["freelancer-disciplines"] },
    { key: ["freelancers"] },
    { key: ["freelancer-detail"] },
  ],
  freelancer_discipline_links: [
    { key: ["freelancers"] },
    { key: ["freelancer-detail"], entityField: "freelancerId" },
  ],
  freelancer_reviews: [
    { key: ["freelancer-reviews"], entityField: "freelancerId" },
    { key: ["freelancers"] },
    { key: ["freelancer-detail"], entityField: "freelancerId" },
  ],
  // VIB-2934: kontaktpersoner på profilen.
  freelancer_contacts: [
    { key: ["freelancer-contacts"], entityField: "freelancerId" },
  ],
  freelancer_comments: [
    { key: ["freelancer-comments"], entityField: "freelancerId" },
  ],
  freelancer_comment_attachments: [
    { key: ["freelancer-comments"], entityField: "freelancerId" },
    // VIB-2934: miniatyrene i loggen.
    { key: ["freelancer-image-urls"], entityField: "freelancerId" },
  ],
  // CRM-2.2: oppfølginger. Query-nøklene har id som 2. element (eier/kunde) slik at
  // entityField treffer; uten kontekst faller invalideringen tilbake til prefikset.
  follow_ups: [
    { key: ["follow-ups"], entityField: "ownerId" },
    { key: ["customer-follow-ups"], entityField: "customerId" },
    { key: ["customer-detail"], entityField: "customerId" },
    // VIB-2891: «Neste steg» i kundeliste v2.
    { key: ["customer-list"] },
  ],
  import_log: [
    { key: ["import-log"] },
  ],
  invoices: [
    { key: ["invoices"] },
    { key: ["project-detail"], entityField: "projectId" },
    // VIB-1290: kundekortet viser fakturahistorikk. Faktura-mutasjoner
    // passerer projectId, ikke customerId — uten customerId faller
    // invalidateFor tilbake til bred ["customer-detail"]-invalidering, som
    // er korrekt (litt bredt, men aldri stale).
    { key: ["customer-detail"], entityField: "customerId" },
    { key: ["active-projects-invoice-summary"] },
    { key: ["invoice-forecast"] },
    { key: ["total-revenue-ytd"] },
    { key: ["total-bf-ytd"] },
    { key: ["pm-inbox"], entityField: "userId" },
    { key: ["employee-dashboard"], entityField: "userId" },
    { key: ["dashboard"] },
    { key: ["sales-unit-hourly-rates"] },
    { key: ["project-lead-hourly-rates"] },
  ],
  monthly_closings: [
    { key: ["monthly-closing-status"] },
  ],
  monthly_confirmations: [
    { key: ["monthly-confirmations"], entityField: "userId" },
    { key: ["billing-rates"], entityField: "year" },
    { key: ["employee-billing-rate"], entityField: "userId" },
  ],
  notification_preferences: [
    { key: ["notification-preferences"] },
  ],
  notifications: [
    { key: ["notifications"], entityField: "userId" },
    { key: ["unread-count"], entityField: "userId" },
  ],
  offer_editors: [
    { key: ["offer-editors"], entityField: "offerId" },
  ],
  // VIB-2979: Grunnlag i tilbudsarbeidsrommet (1:1 med offers).
  offer_workspaces: [
    { key: ["offer-workspace"], entityField: "offerId" },
  ],
  // VIB-2980: Idé-tavlen i tilbudsarbeidsrommet.
  offer_idea_cards: [
    { key: ["offer-idea-cards"], entityField: "offerId" },
  ],
  offer_idea_groups: [
    { key: ["offer-idea-groups"], entityField: "offerId" },
  ],
  offer_line_templates: [
    { key: ["offer-line-templates"] },
    { key: ["admin-offer-line-templates"] },
  ],
  offer_lines: [
    { key: ["offer-lines"], entityField: "offerId" },
    // Kundekort v3: GEEK i pipe regnes fra linjene på kundens sendte tilbud.
    { key: ["customer-offer-geek"], entityField: "customerId" },
    // VIB-2891: GEEK i pipe per kunde i kundeliste v2.
    { key: ["customer-list"] },
  ],
  offer_sections: [
    { key: ["offer-sections"], entityField: "offerId" },
  ],
  offer_template_lines: [
    { key: ["offer-template-lines"], entityField: "templateId" },
  ],
  offer_templates: [
    { key: ["offer-templates"] },
    { key: ["admin-offer-templates"] },
  ],
  offers: [
    { key: ["offers"] },
    { key: ["customer-status"] },
    { key: ["customer-list"] },
    { key: ["customer-offer-geek"], entityField: "customerId" },
    // CRM-2.1: tilbudsendringer flytter prosesser (sync_opportunity_from_offer).
    { key: ["opportunities"] },
    { key: ["offer"], entityField: "offerId" },
    { key: ["sales-dashboard-projects"] },
    { key: ["employee-kpis"], entityField: "userId" },
  ],
  // CRM-2.1: prosesser. ["opportunities"] dekker liste- og kundenøklene
  // (["opportunities", { ... }]); køen og pipen leser i tillegg derivater.
  opportunities: [
    { key: ["opportunities"] },
    { key: ["opportunity"], entityField: "opportunityId" },
    { key: ["customer-detail"], entityField: "customerId" },
  ],
  // VIB-2177: ulest-oppsummeringen er en liste-query (flere prosjekt-IDer i
  // én RPC), så den kan ikke invalideres per prosjekt-ID. Bred invalidering
  // er billig (ett kall) og skjer på både ny kommentar og markert-lest.
  project_comment_reads: [
    { key: ["project-comment-unread"] },
  ],
  project_comments: [
    // VIB-3024: kanallisten i Chat-rommet (siste aktivitet og hvem som er med).
    { key: ["project-channels"] },
    { key: ["project-comments"], entityField: "projectId" },
    { key: ["project-comment-unread"] },
    { key: ["employee-activity"], entityField: "userId" },
  ],
  document_folders: [
    // Mappelista styrer hva som i det hele tatt er synlig, så en
    // tilgangsendring må slå gjennom på innholdslista også.
    { key: ["document-folders"] },
    { key: ["documents"], entityField: "folderId" },
  ],
  documents: [
    { key: ["documents"], entityField: "folderId" },
    // Innholdet hentes per dokument, ikke med lista — se useDocumentContent.
    { key: ["document-content"], entityField: "documentId" },
  ],
  // VIB-2722: medredaktører avgjør om rediger-knappen vises, så en endring
  // her må også friske opp dokumentlista.
  document_editors: [
    { key: ["document-editors"], entityField: "documentId" },
    { key: ["documents"], entityField: "folderId" },
  ],
  // VIB-2721: en gruppeendring kan gjøre en hel mappe synlig eller usynlig,
  // så mappelista må friskes opp. Uten ID: en tildeling kan treffe hvem som
  // helst, og vi vet ikke hvem som sitter med en foreldet liste.
  access_groups: [{ key: ["access-groups"] }, { key: ["document-folders"] }],
  access_group_members: [{ key: ["access-groups"] }, { key: ["document-folders"] }],
  document_folder_grants: [
    { key: ["document-folder-grants"], entityField: "folderId" },
    { key: ["document-folders"] },
  ],
  // VIB-2727: bilder inne i et dokument. Nøkkelen bærer stiene, ikke mappa, så
  // prefikset er bredt — og det er riktig her: signerte adresser lever i 60
  // sekunder, så en bred invalidering koster én ny signering, ikke en
  // nedlasting av arkivet. Innholdet leses ut av `documents`, så en tilknytning
  // må også friske opp dokumentet selv.
  document_images: [
    { key: ["document-images"] },
    { key: ["document-content"], entityField: "documentId" },
  ],
  project_documents: [
    { key: ["project-documents"], entityField: "projectId" },
  ],
  project_events: [
    { key: ["project-events"], entityField: "projectId" },
  ],
  project_extras: [
    { key: ["project-extras"], entityField: "projectId" },
    // VIB-1283: statistlønn-CSV-eksporten er team-bred (ingen projectId),
    // så den er registrert uten entityField — invalideres av enhver
    // project_extras-mutasjon uansett prosjekt.
    { key: ["extras-salary-export"] },
    // VIB-2092: DL-ens utbetalingsliste er team-bred (ingen projectId), så den
    // registreres uten entityField og invalideres av enhver endring.
    { key: ["extras-awaiting-payment"] },
  ],
  project_meetings: [
    { key: ["project-meetings"], entityField: "projectId" },
    { key: ["project-events"], entityField: "projectId" },
  ],
  project_offer_metadata: [
    { key: ["project-detail"], entityField: "projectId" },
  ],
  project_owners: [
    // VIB-3024: eiere er med i prosjektkanalen.
    { key: ["project-channels"] },
    { key: ["project-owners"] },
    { key: ["project-detail"], entityField: "projectId" },
    { key: ["accessible-project-ids"], entityField: "userId" },
    { key: ["bf-targets"], entityField: "year" },
    { key: ["employees"] },
    { key: ["employee-bf"], entityField: "userId" },
    { key: ["employee-profile"], entityField: "userId" },
    { key: ["seller-project-breakdown"], entityField: "userId" },
    { key: ["project-permission"], entityField: "projectId" },
    { key: ["sales-unit-hourly-rates"] },
    { key: ["top-projects"] },
    { key: ["employee-kpis"], entityField: "userId" },
  ],
  project_role_permissions: [
    { key: ["project-role-permissions"] },
    { key: ["project-permission"], entityField: "projectId" },
  ],
  projects: [
    // VIB-3024: prosjektkanalene i Chat-rommet (navn, fase, aktive prosjekter).
    { key: ["project-channels"] },
    // VIB-2990: kapasitetsfordelingen i teamets faktureringsgrad.
    { key: ["capacity-breakdown"] },
    { key: ["product-budget-deviation"] },
    { key: ["project-drill"], entityField: "projectId" },
    { key: ["is-project-lead"] },
    { key: ["projects"] },
    { key: ["customer-status"] },
    // VIB-2891: GEEK levert 12 mnd og «våre kunder» i kundeliste v2.
    { key: ["customer-list"] },
    { key: ["project-detail"], entityField: "projectId" },
    { key: ["active-projects-invoice-summary"] },
    { key: ["project-print"], entityField: "projectId" },
    { key: ["accessible-project-ids"], entityField: "userId" },
    { key: ["budget-deviations"] },
    { key: ["bonus-goals"] },
    { key: ["customer-detail"], entityField: "customerId" },
    // VIB-1290: /kunder viser prosjektantall per kunde — endres et prosjekts
    // kunde-kobling må count-lista re-hentes (bred key, prefiks-matcher
    // ["customers", "with-project-counts"]).
    { key: ["customers"] },
    { key: ["dashboard"] },
    { key: ["employee-dashboard"], entityField: "userId" },
    { key: ["employee-profile"], entityField: "userId" },
    { key: ["employee-value-contribution"], entityField: "userId" },
    { key: ["employee-kpis"], entityField: "userId" },
    { key: ["import-projects"] },
    { key: ["invoice-forecast"] },
    { key: ["my-tasks"], entityField: "userId" },
    { key: ["pm-inbox"], entityField: "userId" },
    { key: ["pm-projects"], entityField: "userId" },
    { key: ["production-kpis"] },
    { key: ["project-comments"], entityField: "projectId" },
    { key: ["project-permission"], entityField: "projectId" },
    { key: ["seller-project-breakdown"], entityField: "userId" },
    { key: ["sync-coverage"] },
    { key: ["next-project-code"] },
    { key: ["sales-dashboard-projects"] },
    { key: ["top-projects"] },
    { key: ["employee-bf"], entityField: "userId" },
    { key: ["commission-deltas"] },
    { key: ["sales-unit-hourly-rates"] },
    { key: ["project-lead-hourly-rates"] },
  ],
  // VIB-1291: recurring_agreements — global liste (/avtaler). Plain-entryen
  // dekker også ["recurring-agreements", { customerId }] via React Querys
  // prefiks-matching (invalidateQueries på et prefiks treffer alle barn).
  // customer-detail invalideres eksplisitt med entityField siden kundekortet
  // (VIB-1290) viser aktive avtaler under en annen queryKey.
  recurring_agreements: [
    { key: ["recurring-agreements"] },
    { key: ["customer-detail"], entityField: "customerId" },
    // VIB-2103: «Lagring»-kolonnen på avsluttede prosjekter leser storage_gb
    // herfra, så en ny lagringsavtale ved prosjektlukking må slå gjennom.
    { key: ["project-storage"] },
  ],
  role_menu_permissions: [
    { key: ["role-menu-permissions"] },
  ],
  sales_unit_members: [
    { key: ["sales-unit-members"], entityField: "salesUnitId" },
    { key: ["sales-units"], entityField: "year" },
    { key: ["seller-project-breakdown"], entityField: "userId" },
    { key: ["employee-bf"], entityField: "userId" },
  ],
  sales_units: [
    { key: ["sales-units"], entityField: "year" },
    { key: ["bf-targets"], entityField: "year" },
    { key: ["employee-dashboard"], entityField: "userId" },
    { key: ["employee-bf"], entityField: "userId" },
    { key: ["sales-unit-hourly-rates"] },
  ],
  sales_display_slides: [
    { key: ["sales-display-slides"] },
  ],
  sidebar_shortcuts: [
    { key: ["sidebar-shortcuts"] },
  ],
  // VIB-1308: trinnpris-satser. Kundekort/avtaler viser aktivt trinn, så en
  // satsendring må også re-hente kundedetaljene.
  storage_pricing_tiers: [
    { key: ["storage-pricing-tiers"] },
    { key: ["customer-detail"], entityField: "customerId" },
  ],
  system_config: [
    { key: ["system-config"], entityField: "key" },
    // VIB-3050: regelpanelet leser verdiene fra system_config.
    { key: ["lead-rules"] },
    { key: ["tm-invoice-data"], entityField: "projectId" },
    // VIB-1396: feature-flag-hooken leser alle feature_*-rader i ett kall og
    // Driftskonfig-seksjonen leser hele tabellen — begge globale (uten
    // entityField) siden en enkelt nøkkel-endring skal invalidere begge lister.
    { key: ["feature-flags"] },
    { key: ["system-config-all"] },
  ],
  task_comments: [
    { key: ["task-comments"], entityField: "assignmentId" },
    { key: ["task-comment-counts"] },
  ],
  time_entries: [
    // VIB-2990: kapasitetsfordelingen i teamets faktureringsgrad.
    { key: ["capacity-breakdown"] },
    { key: ["product-budget-deviation"] },
    { key: ["project-drill"], entityField: "projectId" },
    // CRM-1.6: timer per kunde (kundekortets nøkkeltall).
    { key: ["customer-hours"] },
    { key: ["payroll-basis"] },
    { key: ["time-entries"], entityField: "userId" },
    { key: ["internal-time-entries"], entityField: "projectId" },
    { key: ["tm-invoice-data"], entityField: "projectId" },
    { key: ["task-time-entries"], entityField: "userId" },
    // VIB-2093: en nylagret føring skal fjerne dagen fra popup-en umiddelbart.
    { key: ["missing-hours"] },
    { key: ["my-missing-days"] },
    { key: ["my-tasks"], entityField: "userId" },
    { key: ["bonus-goals"] },
    { key: ["monday-import-gap"] },
    { key: ["available-tasks"], entityField: "userId" },
    { key: ["month-time-overview"], entityField: "userId" },
    { key: ["time-gaps"], entityField: "userId" },
    { key: ["project-detail"], entityField: "projectId" },
    { key: ["billing-rates"], entityField: "year" },
    { key: ["billing-rate-completed-tasks"], entityField: "userId" },
    { key: ["employee-billing-rate"], entityField: "userId" },
    { key: ["budget-deviations"] },
    { key: ["employee-activity"], entityField: "userId" },
    { key: ["employee-dashboard"], entityField: "userId" },
    { key: ["employee-profile"], entityField: "userId" },
    { key: ["employee-value-contribution"], entityField: "userId" },
    { key: ["employee-kpis"], entityField: "userId" },
    { key: ["production-kpis"] },
    { key: ["project-hours"] },
    { key: ["seller-project-breakdown"], entityField: "userId" },
    { key: ["dashboard"] },
    { key: ["top-projects"] },
    { key: ["sales-unit-hourly-rates"] },
    { key: ["project-lead-hourly-rates"] },
  ],
  timebank_transactions: [
    { key: ["timebank-transactions"], entityField: "userId" },
    { key: ["timebank-balance"], entityField: "userId" },
    { key: ["timebank-pending"] },
    { key: ["timebank-all"] },
  ],
  upsells: [
    { key: ["upsells"], entityField: "projectId" },
    { key: ["project-detail"], entityField: "projectId" },
  ],
  user_widget_overrides: [
    { key: ["user-widget-overrides"], entityField: "userId" },
  ],
  users: [
    { key: ["users"] },
    // «Ny kunde»: retten følger av rollen og om brukeren er aktiv.
    { key: ["can-create-customer"] },
    { key: ["users-all"] },
    { key: ["employees"] },
    { key: ["employee-profile"], entityField: "userId" },
    { key: ["user-capacity"], entityField: "userId" },
    // employee-rates is an RPC that reads hourly_cost_rate/hourly_sales_rate/hire_date from users
    { key: ["employee-rates"] },
    { key: ["birthdays-today"] },
    { key: ["birthdays-upcoming"] },
    { key: ["billing-rates"], entityField: "year" },
    { key: ["billing-rate-completed-tasks"], entityField: "userId" },
    { key: ["employee-billing-rate"], entityField: "userId" },
    { key: ["budget-deviations"] },
    { key: ["employee-dashboard"], entityField: "userId" },
    { key: ["monday-users"] },
    { key: ["pm-inbox"], entityField: "userId" },
    { key: ["pm-projects"], entityField: "userId" },
    { key: ["production-kpis"] },
    { key: ["upcoming-tasks"] },
    { key: ["week-tasks"] },
    { key: ["top-projects"] },
    { key: ["project-lead-hourly-rates"] },
    // VIB-2621: users.changelog_seen_id, lest av useChangelogSeen(userId)
    { key: ["changelog-seen"], entityField: "userId" },
    // VIB-2917: users.department_id og can_manage_equipment
    { key: ["equipment-profile"], entityField: "userId" },
  ],
  voucher_account_mappings: [
    { key: ["voucher-account-mappings"] },
  ],
  voucher_allocations: [
    { key: ["voucher-allocations"], entityField: "voucherId" },
    { key: ["project-detail"], entityField: "projectId" },
    // VIB-1204: budsjettpanelets "ført bilagskost"-tall.
    { key: ["voucher-approval-context"], entityField: "projectId" },
  ],
  vouchers: [
    { key: ["tm-invoice-data"], entityField: "projectId" },
    { key: ["project-detail"], entityField: "projectId" },
    { key: ["pm-projects"], entityField: "userId" },
    { key: ["pm-inbox"], entityField: "userId" },
    { key: ["total-bf-ytd"] },
    // VIB-1226: godkjenningsdialogen henter bilaget selv on-demand.
    { key: ["voucher-approval-context"], entityField: "projectId" },
    // VIB-2927: «Fakturert» per frilanser leses fra bilagene.
    { key: ["project-freelancers"], entityField: "projectId" },
    { key: ["freelancer-project-history"] },
    { key: ["freelance-supplier-overview"] },
  ],
  widget_configs: [
    { key: ["widget-configs"] },
  ],
} as const satisfies Registry;
